import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Cocktails } from '@/lib/api/v1';
import { Badge, Card, EmptyState, Icon, Skeleton } from '@/design-system';

/**
 * Cocktail detail — recipe card with method steps + match status.
 *
 * Shows missing ingredients prominently when not makeable so the user has
 * a clear "go shop this" action surface (could later "add to wishlist"
 * directly from missing-ingredient chips).
 */
export function CocktailDetailPage() {
  const { slug = '' } = useParams();

  const cocktailQ = useQuery({
    queryKey: ['cocktail', slug],
    queryFn: () => Cocktails.get(slug),
    enabled: Boolean(slug),
    staleTime: 5 * 60_000,
  });

  if (cocktailQ.isLoading) {
    return (
      <div className="grid gap-4">
        <Skeleton height={28} width="200px" />
        <Skeleton height={120} />
        <Skeleton height={200} />
      </div>
    );
  }

  if (cocktailQ.isError || !cocktailQ.data) {
    return (
      <Card className="p-6">
        <EmptyState icon="warning" title="Cocktailen ble ikke funnet." />
        <Link to="/bar" className="mt-4 inline-block text-sm text-candle hover:underline">
          ← Tilbake til baren
        </Link>
      </Card>
    );
  }

  const match = cocktailQ.data;
  const r = match.recipe;

  return (
    <div className="grid gap-6 max-w-3xl">
      <div>
        <Link
          to="/bar"
          className="inline-flex items-center gap-2 text-sm text-mist hover:text-candle"
        >
          <Icon name="chevron-left" size={14} /> Tilbake til baren
        </Link>
      </div>

      <header className="grid gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {match.canMake ? (
            <Badge tone="gold">
              <Icon name="check-circle" size={10} className="mr-1 inline" /> Du har alt
            </Badge>
          ) : (
            <Badge tone="ruby">
              Mangler {match.missingCount} {match.missingCount === 1 ? 'ingrediens' : 'ingredienser'}
            </Badge>
          )}
          {r.ibaClassic && <Badge tone="gold">IBA Classic</Badge>}
          <Badge tone="mist">{r.base}</Badge>
          {r.tags.slice(0, 3).map((t) => (
            <Badge key={t} tone="mist">{t}</Badge>
          ))}
        </div>
        <h1 className="font-serif text-4xl text-parchment">{r.name}</h1>
        <p className="text-sm text-mist max-w-2xl">{r.description}</p>
      </header>

      {!match.canMake && match.gaps.length > 0 && (
        <Card className="grid gap-2 p-4 border-[color:var(--ui-color-danger)]/30">
          <div className="flex items-center gap-2">
            <Icon name="warning" className="text-[color:var(--ui-color-danger)]" size={16} />
            <h3 className="font-serif text-base text-parchment">Du mangler:</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {match.gaps.map((g) => (
              <span
                key={g.role}
                className="rounded-md border border-line bg-ink-soft px-3 py-1 text-sm text-parchment"
              >
                {g.display}
              </span>
            ))}
          </div>
          <p className="text-xs text-mist">
            Tips: pantry-ingredienser som lime, sitron, soda og enkel sukkerlake regnes som tilgjengelige.
          </p>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        {/* Ingredients */}
        <Card className="grid gap-3 p-5">
          <div className="flex items-center gap-2">
            <Icon name="list" className="text-candle" size={16} />
            <h2 className="font-serif text-xl text-parchment">Ingredienser</h2>
          </div>
          <ul className="grid gap-2">
            {r.ingredients.map((ing, idx) => {
              const missing = match.gaps.some((g) => g.role === ing.role);
              return (
                <li
                  key={`${ing.role}-${idx}`}
                  className={`flex items-baseline justify-between gap-3 border-b border-line pb-2 last:border-b-0 ${
                    missing ? 'opacity-100' : ''
                  }`}
                >
                  <span className="text-sm text-parchment flex-1">
                    {ing.display}
                    {ing.optional && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-mist">
                        valgfritt
                      </span>
                    )}
                  </span>
                  <span className="text-sm text-mist tabular-nums shrink-0">
                    {formatAmount(ing.amount, ing.unit)}
                  </span>
                  {missing && (
                    <Icon name="x-circle" size={14} className="text-[color:var(--ui-color-danger)] shrink-0" />
                  )}
                </li>
              );
            })}
          </ul>
          <div className="grid gap-1 border-t border-line pt-3 text-xs">
            <div className="flex items-center gap-2 text-mist">
              <Icon name="container" size={12} />
              <span>Glass: <span className="text-parchment">{r.glassware}</span></span>
            </div>
            <div className="flex items-center gap-2 text-mist">
              <Icon name="snow" size={12} />
              <span>Is: <span className="text-parchment">{r.ice}</span></span>
            </div>
            {r.garnish && (
              <div className="flex items-center gap-2 text-mist">
                <Icon name="sparkles" size={12} />
                <span>Pynt: <span className="text-parchment">{r.garnish}</span></span>
              </div>
            )}
          </div>
        </Card>

        {/* Method */}
        <Card className="grid gap-3 p-5">
          <div className="flex items-center gap-2">
            <Icon name="chef-hat" className="text-candle" size={16} />
            <h2 className="font-serif text-xl text-parchment">Slik gjør du det</h2>
          </div>
          <ol className="grid gap-3">
            {r.method.map((step, idx) => (
              <li key={idx} className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-candle/10 text-xs text-candle font-medium">
                  {idx + 1}
                </span>
                <span className="text-sm text-parchment leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
          <div className="border-t border-line pt-3">
            <span className="text-[10px] uppercase tracking-widest text-mist">Vanskelighetsgrad</span>
            <div className="mt-1 flex gap-1">
              {[1, 2, 3].map((i) => (
                <Icon
                  key={i}
                  name="star"
                  size={14}
                  className={i <= r.difficulty ? 'text-candle' : 'text-mist/30'}
                />
              ))}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function formatAmount(amount?: number, unit?: string): string {
  if (amount == null) return unit ?? '';
  if (unit === 'to-top') return 'topp opp';
  if (unit === 'splash') return 'splash';
  if (unit === 'dash') return amount === 1 ? '1 dash' : `${amount} dashes`;
  if (unit === 'drops') return `${amount} dråper`;
  if (unit === 'cube') return amount === 1 ? '1 sukkerbit' : `${amount} sukkerbiter`;
  if (unit === 'sprig') return `${amount} kvist${amount === 1 ? '' : 'er'}`;
  if (unit === 'piece') return `${amount} stk`;
  if (unit === 'rim') return 'kant';
  if (unit === 'barspoon') return `${amount} barskje`;
  if (unit === 'oz') return `${amount} oz`;
  if (unit === 'cl') return `${amount} cl`;
  return `${amount} ${unit ?? 'ml'}`;
}
